import { useNavigate, useSearchParams } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { withZod } from '@remix-validated-form/with-zod'
import { compact, isEmpty, isEqual } from 'lodash'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { z } from 'zod'
import { ArrowLeftIcon } from '@phosphor-icons/react'
import { Button } from 'app/components/ui/button'
import { DialogActionBar } from 'app/components/ui/dialog'
import { useConfirmation } from '../ConfirmationContext'
import FormInput from '../form/FormInput'
import FormInputWrapper from '../form/FormInputWrapper'
import { SmartForm } from '../form/SmartForm'
import { seedFilters } from './customPageSeed'
import { type FilterType, emptyFilter } from 'app/components/ui/table-filters/use-filters-container'
import { FiltersContainerView as FiltersContainer } from 'app/components/ui/table-filters/filters-container'
import { decodeFilters, FilterableColumn, FilterObject } from 'app/filter.utils'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useListCopy } from 'app/hooks/useListCopy'
import { ACTION_CENTER_FLAG } from 'app/components/action-center/systemPipelines'
import { ListVisibilityField } from 'app/components/lists/ListVisibilityField'
import type { List } from 'app/generated/graphql'
import type { CustomPageJson, CustomPageType, CustomPages } from 'app/types/entities'
import type { Workspace } from 'app/types/entities'
import type { ListVisibility } from 'app/types/list-visibility'
import { closeModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useCustomPageStore, useListStore, usePipelineStore } from 'app/stores/StoreContext'
import {
  dispatchCustomPageCreated,
  setLastCreatedPage,
  addPendingPageId,
  markCustomPageDeleted,
} from 'lib/customPageEvents'
import { PROCEDURE_FILTER_COLUMNS } from 'app/components/action-center/procedureFilter'
import { getFilterableColumns as activitiiesFilterableColumns } from 'app/table-columns/activities'
import { appointmentFilterableColumns } from 'app/table-columns/appointments'
import { filterableColumns as contactsFilterableColumns } from 'app/table-columns/contacts'
import { getFilterableColumns as tasksFilterableColumns } from 'app/table-columns/tasks'
import { EMPTY_PROVIDER_DIRECTORY } from 'lib/appointmentsPage'
import { RECORD_TYPES } from 'lib/recordTypes'
import { hasRole, useUser, useWorkspace } from 'app/utils'
import { comActivityTypes } from 'lib/comActivityTypes'
import { companionFor } from 'lib/companionPipelines'
import { isSMCOrg } from 'lib/isSMCOrg'
import { debug, logError } from 'lib/logging'
import { canChangeVisibility, canDeleteList, isPersonalList } from 'lib/listVisibility'
import { isFilterList, listRuleFilters } from 'lib/practiceLists'
import { LIST_ENTITY_TYPE, savedPageOfList } from 'lib/savedLists'
import { Jsonify } from 'lib/toJSON'

const listSchema = withZod(
  z.object({
    name: z
      .string()
      .min(1, { message: `Name is required` })
      .max(255, { message: `Name must be less than 255 characters` })
      .transform((value) => value.replace(/\//g, ` `)),
  }),
)

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === `object` && value !== null
}

const getSavedCustomPage = (
  data: unknown,
  key: `createCustomPage` | `updateCustomPage`,
): (CustomPageType & { error?: string }) | undefined => {
  if (!isRecord(data)) return undefined
  const value = data[key]
  if (!isRecord(value) || typeof value.id !== `string`) return undefined
  return value as CustomPageType & { error?: string }
}

export type CustomPageOptions = {
  page: CustomPages
  subpage?: CustomPageJson
  filters?: FilterableColumn[]
  onDelete?: () => void
  onPageCreated?: () => void
  /** A template's name, offered as the default. */
  initialName?: string
  /** A template's rule, seeded into the builder once the page's columns are known. */
  initialFilters?: FilterObject[]
  /** A step to go back to, as in the New list wizard: drawn where a saved list's Delete sits. */
  onBack?: () => void
  /** Where a save goes: a custom page, or a List. */
  saveTo?: `customPage` | `list`
  /** The List being edited when `saveTo` is `list`; without one, a save creates it. */
  list?: List
}

/**
 * @description Internal component that renders the form to create a new page.
 * It validates the name is not empty and is less than 255 characters.
 * If selected filters are empty or the name is empty, it will show an error message.
 * If selected filters are not empty and the name is not empty, it will call the API to create the page.
 * It validates selected filters have attribute, operation and value if not it will ignore the filter or show an error message if a user only selected one filter.
 * @returns ReactElement
 */
// observer: reads a feature toggle (and possibly authStore.user) in render; useFeatureToggle resolves async and its contract requires an observer wrapper (TRK-6020).
export const CustomPageForm = observer(function CustomPageForm({
  page,
  subpage,
  filters,
  onDelete,
  onPageCreated,
  initialName,
  initialFilters,
  onBack,
  saveTo = `customPage`,
  list: listProp,
}: CustomPageOptions): ReactElement {
  const me = useUser()
  const [confirm] = useConfirmation()
  const [loading, setLoading] = useState<boolean>(false)
  const [deleteInProgress, setDeleteInProgress] = useState<boolean>(false)
  const staffV2Enabled = useFeatureToggle(`staff-v2`)
  const actionCenterEnabled = useFeatureToggle(ACTION_CENTER_FLAG)
  const copy = useListCopy()
  const checkPermission = useCheckPermission()
  const navigation = useNavigate()
  const [searchParams] = useSearchParams()
  const workspace: Jsonify<Workspace> | undefined = useWorkspace()
  const customPageStore = useCustomPageStore()
  const pipelineStore = usePipelineStore()
  const listStore = useListStore()
  const savesToList = saveTo === `list`
  // A List is only the thing being edited when saves go to Lists, so save, delete and the form's
  // seeding can never disagree about what it is.
  const list = savesToList ? listProp : undefined
  // A new list is the creator's own to set; an existing one only its creator may switch.
  const offersVisibility = savesToList && (!list || canChangeVisibility(list, me?.id))
  const [visibility, setVisibility] = useState<ListVisibility>(list && isPersonalList(list) ? `personal` : `shared`)
  const [selectedFilters, setSelectedFilters] = useState<FilterType[] | undefined>(() => {
    if (compact(subpage?.filters).length > 0) {
      return seedFilters(subpage!.filters! as never, filters ?? [])
    }
    return undefined
  })
  const initialJoin = list?.join === `or` ? `or` : `and`
  const [join, setJoin] = useState<`and` | `or`>(initialJoin)
  const [nameError, setNameError] = useState<string | undefined>(undefined)
  const [filterError, setFilterError] = useState<string | undefined>(undefined)
  // `me`-dependent values live inside getColumns (rather than as render-scoped consts) so
  // the function has no forward references: the seeding effect below needs to call it
  // before the `me` guard that used to sit above them.
  const getColumns = () => {
    if (!me) return []
    const isSMC = isSMCOrg(me.orgId!)
    const isSuper = me.type === `super`
    // Legacy role-based logic (staff-v2 OFF)
    const legacyAddMarketingFilters =
      isSuper || (isSMC && hasRole(me, `orgAdmin`)) || (!isSMC && !hasRole(me, `orgAssistant`))
    // Permission-based logic (staff-v2 ON)
    const permissionAddMarketingFilters = isSuper || checkPermission(`org:marketing:advanced-filters`)
    const addMarketingFilters = staffV2Enabled ? permissionAddMarketingFilters : legacyAddMarketingFilters
    // Permission-based check for isWorkspaceUser (not org admin)
    const isNotOrgAdmin = staffV2Enabled ? !checkPermission([`org:settings:manage-users`]) : !hasRole(me, `orgAdmin`)
    if (page === `contacts`) {
      return contactsFilterableColumns({
        addMarketingFilters,
        isOrgView: !workspace,
        type:
          (decodeFilters(searchParams.get(`filters`)).find((filter) => filter.attribute === `platform`)
            ?.value as string) ?? ``,
        isSMC,
        isSuper,
        isWokspaceUser: isNotOrgAdmin,
        staffV2Enabled,
        actionCenterEnabled,
        checkPermission,
        copy,
      })
    } else if (page === `activities`) {
      return activitiiesFilterableColumns({
        comActivityTypes,
        addMarketingColumns: addMarketingFilters,
        addFollowUp: true,
      })
    } else if (page === `tasks`) {
      return tasksFilterableColumns()
    } else if (page === `treatmentPlans`) {
      // What the Treatment Plans page itself filters on: the treatment's code or description,
      // and the location.
      return PROCEDURE_FILTER_COLUMNS
    } else if (page === `appointments`) {
      // The rule editor has no provider list, so its Provider filter offers no names.
      return appointmentFilterableColumns({ preAppliedFilters: {}, providers: EMPTY_PROVIDER_DIRECTORY })
    }
    return []
  }
  // A List's rule is seeded the way a template's is, from the columns this page filters on.
  const seedRule = useMemo(
    () => initialFilters ?? (list && isFilterList(list) ? listRuleFilters(list) : undefined),
    [initialFilters, list],
  )
  // The template's rule can only be matched to columns once `me` has loaded, so it seeds
  // in an effect rather than in the initializer that the subpage path uses. `me` is in the
  // deps so the effect re-runs once auth fills in, instead of running once at mount and
  // never recovering if `me` was still falsy on that first render.
  useEffect(() => {
    if (!me) return
    if (!seedRule || selectedFilters !== undefined) return
    setSelectedFilters(seedFilters(seedRule, getColumns()))
  }, [seedRule, me])

  if (!me) return <></>

  const handleSubmit = async () => {
    setLoading(true)
    let form = document.getElementById(`create-new-list`) as HTMLFormElement
    let data = Object.fromEntries(new FormData(form).entries())
    let result = await listSchema.validate(data)
    if (result.error) {
      debug(`customPages`, `error`, result.error)
      if (result?.error?.fieldErrors?.name) {
        setNameError(result?.error?.fieldErrors?.name)
      }
      setLoading(false)
      return
    } else if (nameError) {
      setNameError(undefined)
    }
    if (!selectedFilters?.length || isEqual(selectedFilters, [emptyFilter])) {
      setFilterError(`Select at least one filter`)
      setLoading(false)
      return
    } else if (filterError) {
      setFilterError(undefined)
    }
    debug(`customPages`, `new page data`, data)
    let filterObjects = selectedFilters
      ?.map<Partial<FilterObject>>((filter) => ({
        attribute: filter.attribute!,
        operator: filter.operator!,
        value: filter.value,
      }))
      .reduce((acc: Partial<FilterObject>[], filter) => {
        if (filter.attribute && filter.operator && filter.value) {
          return [...acc, filter]
        } else return acc
      }, [])
    if (isEmpty(filterObjects)) {
      setFilterError(`Select at least one filter with all the fields filled`)
      setLoading(false)
      return
    }
    debug(`customPages`, `filterObjects`, filterObjects)
    const input = { name: String(data.name), page, join, filters: JSON.stringify(filterObjects) }
    if (savesToList) {
      const listInput = { name: input.name, join, filters: input.filters, ...(offersVisibility ? { visibility } : {}) }
      const saveResult = list
        ? await listStore.update(list.id, listInput)
        : await listStore.create({ ...listInput, entityType: LIST_ENTITY_TYPE[page] })
      const saved = saveResult.data as { createList?: List; updateList?: List } | undefined
      const savedList = saved?.updateList ?? saved?.createList
      if (savedList?.id) {
        notifySuccess(`Page ${list ? `updated` : `saved`} successfully`)
        // The list page resolves a list from the store's own read, which is cache-first.
        await listStore.fetchList(undefined, { requestPolicy: `network-only` })
        // The record pages hold their saved lists themselves; this tells them about a new one.
        if (!list && page !== `contacts`) dispatchCustomPageCreated(savedPageOfList(savedList, page))
        if (onPageCreated) onPageCreated()
        const wId = searchParams.get(`wId`)
        navigation(
          page === `contacts`
            ? `/lists/${savedList.id}${wId ? `?wId=${wId}` : ``}`
            : `${RECORD_TYPES[page].basePath}?tab=${savedList.id}${wId ? `&wId=${wId}` : ``}`,
        )
      } else {
        notifyError(`There was an error creating your page: ${data.name}. ${saveResult.error?.message ?? ``}`)
      }
      closeModal()
      return
    }
    const saveResult = subpage?.id
      ? await customPageStore.update(subpage.id, input)
      : await customPageStore.create(input)
    const customPage = getSavedCustomPage(saveResult.data, subpage?.id ? `updateCustomPage` : `createCustomPage`)
    if (customPage && customPage.id) {
      debug(`customPages`, `resultData`, customPage)
      notifySuccess(`Page ${subpage?.id ? `updated` : `saved`} successfully`)
      if (!subpage?.id) {
        addPendingPageId(customPage.id!)
        dispatchCustomPageCreated(customPage as CustomPageType)
        setLastCreatedPage(customPage as CustomPageType)
      }
      if (onPageCreated) onPageCreated()
      navigation(
        `${RECORD_TYPES[page].basePath}?tab=${customPage.id}${searchParams.has(`wId`) ? `&wId=${searchParams.get(`wId`)}` : ``}`,
      )
    } else {
      debug(`customPages`, `error`, customPage)
      notifyError(`There was an error creating your page: ${data.name}. ${customPage?.error}`)
    }
    closeModal()
  }
  // The 728px floor is a desktop width: below md it pushed the whole dialog off a phone's
  // screen, while the filter rows inside already stack on their own there.
  const deleteList = () =>
    confirm({
      title: `Delete list`,
      description: `Are you sure you want to delete this list?`,
      variant: `danger`,
    }).then(async () => {
      setDeleteInProgress(true)
      if (list) {
        const deleted = await listStore.remove(list.id)
        if (deleted.error) {
          setDeleteInProgress(false)
          notifyError(`There was an error deleting your list: ${list.name}`)
          debug(`customPages`, `error deleting a list`, deleted.error)
          return
        }
        await listStore.fetchList(undefined, { requestPolicy: `network-only` })
        markCustomPageDeleted(list.id)
        closeModal()
        onDelete?.()
        return
      }
      const deleteResult = await customPageStore.remove(subpage!.id!)
      if (deleteResult.error) {
        setDeleteInProgress(false)
        notifyError(`There was an error deleting your subpage: ${subpage!.name}`)
        debug(`customPages`, `error deleting subpage for ${page} page`, deleteResult.error)
      } else {
        markCustomPageDeleted(subpage!.id!)
        closeModal()
        // A contacts list's stages live on a companion list named after it, which
        // nothing can reach or name once the list itself is gone.
        if (page === `contacts` && subpage!.id) {
          try {
            if (pipelineStore.list === undefined) await pipelineStore.fetchList()
            const companion = companionFor(subpage!.id, pipelineStore.hits)
            if (companion?.id) await pipelineStore.remove(companion.id)
          } catch (err) {
            logError(`Error deleting a filter list's companion list`, err)
          }
        }
        onDelete?.()
      }
    })

  return (
    // The form draws the whole dialog body and its footer, so every opener passes
    // `removePadding`: the footer runs edge to edge and its rounded corners meet the dialog's.
    // 800px from md up leaves the filter box, inside the body's own gutter, room for a full
    // rule row (attribute, operator, value, remove) without the remove button spilling out.
    <div className="min-h-min overflow-visible md:min-w-200">
      <SmartForm<CustomPageJson>
        id="create-new-list"
        // `max-w-none` lifts the form's own 768px cap, which would otherwise undo the width above.
        className="mb-0 mt-0 flex w-full max-w-none flex-col gap-y-0"
        validator={listSchema}
        defaultValues={subpage}
        preventSubmit
        hideButtons
      >
        <div className="col-span-6 flex flex-col gap-5 px-6 py-5">
          <FormInput
            name="name"
            label="Name"
            placeholder="e.g. Crowns to schedule"
            cols={6}
            error={nameError}
            defaultValue={list?.name ?? subpage?.name ?? initialName}
          />
          {offersVisibility && (
            <div className="col-span-6">
              <ListVisibilityField label="Who can see it" value={visibility} onChange={setVisibility} />
            </div>
          )}
          <FormInputWrapper name="filters" label="Filters" cols={6} className="text-ui-sm" error={filterError}>
            <p className="mb-2 text-body-xs text-subtle">
              {`Every one of these ${RECORD_TYPES[page].label.toLowerCase()} that matches is on the list, and it keeps itself up to date.`}
            </p>
            <div className="z-50 rounded-lg border border-default shadow-elevation-1">
              <FiltersContainer
                columns={getColumns()}
                appliedFilters={selectedFilters}
                onFilterChange={setSelectedFilters}
                onJoinChange={setJoin}
                defaultJoin={initialJoin}
              />
            </div>
          </FormInputWrapper>
        </div>
        {/* Sticky, so a rule long enough to scroll the dialog never hides Save. */}
        <DialogActionBar
          className="sticky bottom-0 col-span-6"
          start={
            onBack ? (
              <Button type="button" variant="ghost" size="md" onClick={onBack}>
                <ArrowLeftIcon size={14} weight="bold" /> Back
              </Button>
            ) : (list ? canDeleteList(list, me.id, checkPermission(`lists:delete-shared`)) : subpage?.id) ? (
              <Button type="button" variant="danger" size="md" loading={deleteInProgress} onClick={deleteList}>
                Delete
              </Button>
            ) : undefined
          }
        >
          <Button type="button" variant="secondary" size="md" onClick={() => closeModal()}>
            Cancel
          </Button>
          <Button type="button" variant="primary" size="md" onClick={handleSubmit} loading={loading}>
            Save
          </Button>
        </DialogActionBar>
      </SmartForm>
    </div>
  )
})
